import React from 'react';
import PropTypes from 'prop-types';
import DataManager from '@americanexpress/data-manager';
import SharedScript, {
  Adobe,
  Contentsquare,
  Ensighten,
  // ClickToChat,
  GtagWrapper,
} from '@americanexpress/shared-scripts';
import { AmexDDLRegister, getAmexDataManagerConfig } from '@americanexpress/data-manager-utils';
import { DDLRegister } from '@americanexpress/react-ddl';
import { ExtendReactlytics } from '@americanexpress/reactlytics';
import { useSelector } from 'react-redux';

/**
 * AnalyticsWrapper is a wrapper component that includes all the necessary analytics scripts
 * and registers the page view event.
 * It also includes the ExtendReactlytics component which is necessary for the analytics to work.
 * The identifier prop is used to identify the analytics event.
 * The shouldDisablePageView prop is used to disable the page view event.
 * If shouldDisablePageView is true, the page view event will not be sent to analytics.
 * */

const AnalyticsWrapper = ({
  route,
  identifier,
  children,
}) => {
  const locale = useSelector((state) => state.getIn(['intl', 'activeLocale']));
  const [language, country] = locale.split('-');
  const loadedHolocrons = useSelector((state) => state.getIn(['holocron', 'loaded']));
  const globalHeaderData = useSelector((state) => state.getIn(['modules', 'axp-global-header']));
  const isGlobalHeaderLoaded = loadedHolocrons && loadedHolocrons.has('axp-global-header');
  const isLoggedInCardMember = isGlobalHeaderLoaded && globalHeaderData.get('authenticated');

  const obj = global.BROWSER && route.shouldDisablePageView ? {
    initialDDL: {
      historyLimit: 700,
      ddl: {
        diff: ['events'],
        version: '1.7.0',
        charset: 'UTF-8',
        user: {},
        events: [],
        components: {},
        campaigns: {},
        timestamp: new Date().toISOString(),
        view: {
          country,
          language,
          locale,
          location: window.location.href,
          timestamp: new Date().toISOString(),
          origin: window.location.hostname,
          title: route.pageTitle,
          viewId: window.location.pathname,
        },
      },
    },
  } : {};
  const { onTrackCall, ddlProviderProps, reactlyticsProps } = getAmexDataManagerConfig({
    ...obj,
  });
  return (
    <DataManager
      onTrackCall={onTrackCall}
      ddlProviderProps={ddlProviderProps}
      reactlyticsProps={reactlyticsProps}
    >
      {route.shouldDisablePageView ? (
        <AmexDDLRegister disableRegisterList={{ view: true }} />
      ) : (
        <AmexDDLRegister />
      )}
      <DDLRegister name="user.authenticated" value={isLoggedInCardMember} />
      <SharedScript name="one-stream-data-handler" windowCheck="oneStreamData" version="^0.1.2" />
      <Adobe version="^1.2.0" />
      <Contentsquare version="^1.0.2" />
      <Ensighten version="^1.2.5" />
      <GtagWrapper version="^1.0.0" />
      {/* <ClickToChat
        version="^1.0.0"
        market={country}
        locale={locale}
        lineOfBusiness="personal_acquisition"
      /> */}
      {/* Only necessary in a root module, otherwise this is handled by oneAmexWrapper */}
      <ExtendReactlytics analytics={{ event: { identifier } }}>
        {children}
      </ExtendReactlytics>
    </DataManager>
  );
};

AnalyticsWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  identifier: PropTypes.string,
  route: PropTypes.shape({
    shouldDisablePageView: PropTypes.bool,
    pageTitle: PropTypes.string,
  }).isRequired,
};

AnalyticsWrapper.defaultProps = {
  identifier: 'axp-international-shop-root',
};

export default AnalyticsWrapper;
